import cn from 'classnames';

import coinSvg from '@assets/svg/coin.svg';
import crownSvg from '@assets/svg/crown.svg';
import coinAnaSvg from '@assets/svg/coin-ana.svg';
import coinDuelSvg from '@assets/svg/coin-duel.svg';
import readyAudio from '@assets/audio/ready.mp3';
import errorAudio from '@assets/audio/error.mp3';
import { ReactComponent as CopyBtn } from '@assets/svg/copy icon.svg';

import FlippableCoin from '@components/coinflip/FlippableCoin/FlippableCoin';
import CoinflipCounter from '@components/coinflip/coinflip-counter';
import { useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import { copyToClipboard, toastErrorMessage, toastSuccessMessage } from '@utils/utils';
import useJoinCoinflipGame from '@hooks/useJoinCoinflipGame';
import useCancelCoinflipGame from '@hooks/useCancelCoinflipGame';
import useSound from 'use-sound';

import { ICoinflipGame, TCoinflipResult } from '@api/types/Coinflip';
import useReduxState from '@hooks/useReduxState';

export default function CoinflipMiddle({
  setGameResult,
  styles,
  socket,
  game: coinflip,
}: {
  // eslint-disable-next-line no-unused-vars
  setGameResult: (result: TCoinflipResult) => void;
  styles: { readonly [key: string]: string };
  socket: Socket;
  game: ICoinflipGame;
}): JSX.Element {
  const [user, setUser] = useReduxState((state) => state.userData);
  const [doFlip, setDoFlip] = useState(false);
  const [doCount, setDoCount] = useState(0);
  const [doResult, setDoResult] = useState(coinflip.game_metadata.status === 'finished');
  const flipStatus = useRef(coinflip.game_metadata.status);
  const [flipResult, setFlipResult] = useState(coinflip.result);
  const [hasCopied, setHasCopied] = useState<boolean>(false);

  useEffect(() => {
    // compare against the previous state to begin the flip animation
    if (flipStatus.current !== 'finished' && coinflip.game_metadata.status === 'finished') {
      flipStatus.current = 'finished';
      setDoCount(new Date().getTime());
    }

    if (coinflip.result) {
      setFlipResult(coinflip.result);
    }
  }, [coinflip]);

  const [playErrorSound] = useSound(errorAudio, { interrupt: true });
  const readysound = useRef<HTMLAudioElement>(new Audio(readyAudio));

  const joinCoinflipGame = useJoinCoinflipGame(coinflip, {
    onCompleted: (game) => {
      setUser({
        type: 'USER_UPDATE_BALANCE',
        arg: {
          balance: game.stake,
          type: 'substract',
        },
      });

      socket.emit('Join-Coinflip', game);
      toastSuccessMessage('Succesfully joined game');
    },
    onError: (e) => {
      toastErrorMessage(e.message);

      playErrorSound();
    },
  });

  const cancelCoinflipGame = useCancelCoinflipGame(coinflip, {
    onCompleted: () => {
      setUser({
        type: 'USER_UPDATE_BALANCE',
        arg: {
          balance: coinflip.stake,
          type: 'add',
        },
      });

      readysound.current.play();
      socket.emit('Cancel-Coinflip', { gameUid: coinflip.uid });
      toastSuccessMessage('Successfully deleted coinflip game');
    },
    onError: (e) => {
      toastErrorMessage(e.message);
      playErrorSound();
    },
  });

  const isUserInGame = (coinflip.tails_user?.id === user.id || coinflip.heads_user?.id === user.id) && coinflip.game_metadata.status !== 'finished';

  const isGameHost = coinflip.game_metadata.creator.id === user.id && coinflip.game_metadata.status !== 'finished';

  const handleClickJoin = async () => {
    readysound.current.play();
    setTimeout(async () => {
      await joinCoinflipGame();
    }, 130);
  };

  const handleCancelGame = async () => {
    await cancelCoinflipGame();
  };
  const handleCopytoClipBoard = async () => {
    await copyToClipboard(`${window.location.href}/${coinflip.uid}`)
      .then(() => {
        toastSuccessMessage('successfully copied to clipboard');
        setHasCopied(true);
        console.log(hasCopied);
      })
      .catch(() => toastErrorMessage('failed to copy to keyboard'));
  };

  return (
    <div className={styles.middleColumn}>
      {doFlip && <FlippableCoin result={coinflip.result} />}
      {doResult && (
        <div
          className={cn(styles.coin, {
            [styles.duel]: flipResult === 'heads',
            [styles.ana]: flipResult === 'tails',
          })}
        >
          <img className={styles.crown} src={crownSvg} alt='winning side' />

          {flipResult === 'heads' && (
            <>
              <img src={coinDuelSvg} className={styles.icon} />
            </>
          )}
          {flipResult === 'tails' && <img src={coinAnaSvg} className={styles.icon} />}
        </div>
      )}
      {doCount > 0 && (
        <CoinflipCounter
          started={true}
          countStart={3}
          startedAt={doCount}
          onEnd={() => {
            setDoCount(0);
            setDoFlip(true);
            setTimeout(() => {
              setGameResult(flipResult);
            }, 2000);
            setTimeout(() => {
              setDoResult(true);
              setDoFlip(false);
            }, 4000);
          }}
          isUserPlaying={coinflip.tails_user?.id === user.id || coinflip.heads_user?.id === user.id}
        />
      )}

      {coinflip.game_metadata.status === 'active' && !isUserInGame && (
        <button
          className={cn(styles.button, {
            [styles.button__active]: coinflip.game_metadata.status == 'active',
          })}
          onClick={handleClickJoin}
        >
          Join
          <div className={styles.stake}>
            <img src={coinSvg} alt='coin icon' />
            <span className={styles.value}>{coinflip.stake.toLocaleString('en-US')}</span>
          </div>
        </button>
      )}
      {coinflip.game_metadata.status == 'active' && isUserInGame && isGameHost && (
        <div className={styles.cancel}>
          <button
            className={cn(styles.button_cancel, {
              [styles.button__active]: coinflip.game_metadata.status == 'active',
            })}
            onClick={handleCancelGame}
          >
            Cancel
            <div className={styles.stake}>
              <img src={coinSvg} alt='coin icon' />
              <span className={styles.value}>{coinflip.stake.toLocaleString('en-US')}</span>
            </div>
          </button>
          {coinflip.game_metadata.is_private && (
            <>
              <div className={styles.copy_btn} onClick={handleCopytoClipBoard}>
                <CopyBtn />
              </div>
              {hasCopied && (
                <div className={styles.copy}>
                  <h3>Link Copied</h3>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
